import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useLayoutEffect, useRef } from "react";
import ProductCard from "./components/ProductCard";
import React from "react";

const ProductSlider = ({ products }) => {
  const scrollContainerRef = useRef(null);
  const cardsContainerRef = useRef(null);
  const isScrollingRef = useRef(false);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);

  // Scroll to the next card (one product at a time)
  const scrollToNextCard = (direction) => {
    const container = scrollContainerRef.current;
    const cardsContainer = cardsContainerRef.current;
    if (!container || !cardsContainer) return;

    const cards = cardsContainer.children;
    if (cards.length === 0) return;

    // Find the card whose centre is closest to the centre of the viewport.
    // This avoids depending on how many cards are fully/partially visible and
    // guarantees we move exactly one card per interaction visually.
    const containerCenter = container.scrollLeft + container.clientWidth / 2;

    let currentPhysicalIndex = 0;
    let smallestDistance = Number.POSITIVE_INFINITY;

    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(cardCenter - containerCenter);

      if (distance < smallestDistance) {
        smallestDistance = distance;
        currentPhysicalIndex = i;
      }
    }

    const targetPhysicalIndex = currentPhysicalIndex + direction;
    if (targetPhysicalIndex < 0 || targetPhysicalIndex >= cards.length) return;

    const targetCard = cards[targetPhysicalIndex];
    if (!targetCard) return;

    // On xl screens, use a fixed one-card step (based on the distance between
    // the current card and the target card). This keeps the 3-card grid
    // alignment and avoids tiny rounding errors from strict centring.
    // On smaller screens, keep the existing "centre the card" behaviour.
    const isXL =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(min-width: 1684px)").matches;

    let targetScrollLeft;

    if (isXL) {
      const currentCard = cards[currentPhysicalIndex];
      if (!currentCard) return;

      const delta = targetCard.offsetLeft - currentCard.offsetLeft;
      targetScrollLeft = container.scrollLeft + delta;
    } else {
      // Center the target card in the viewport for consistent behavior
      // on non-xl breakpoints
      const targetLeft = targetCard.offsetLeft;
      const targetWidth = targetCard.offsetWidth;
      targetScrollLeft =
        targetLeft + targetWidth / 2 - container.clientWidth / 2;
    }

    // Smooth scroll to target position
    isScrollingRef.current = true;
    container.scrollTo({
      left: targetScrollLeft,
      behavior: "smooth",
    });

    // Reset the scrolling flag after animation completes
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 500);
  };

  // Set up infinite scroll effect
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || products.length === 0) return;

    const initializeScroll = () => {
      const scrollWidth = container.scrollWidth;
      if (scrollWidth === 0) {
        // If dimensions are 0, try again after a short delay
        setTimeout(initializeScroll, 50);
        return;
      }

      // Calculate the width of one set of products
      const singleSetWidth = scrollWidth / 3;

      // Move to the start of the middle set (second set)
      // Doing this in useLayoutEffect avoids a visible post-load shift
      container.scrollLeft = singleSetWidth;
    };

    const handleScroll = () => {
      // Skip infinite scroll reset during programmatic scrolling or dragging
      if (isScrollingRef.current || isDraggingRef.current) return;

      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;

      // Calculate the width of one set of products
      const singleSetWidth = scrollWidth / 3;

      // If scrolled to the end (third set), reset to middle (second set)
      if (scrollLeft >= singleSetWidth * 2 - 50) {
        isScrollingRef.current = true;
        container.scrollLeft =
          singleSetWidth + (scrollLeft - singleSetWidth * 2);
        setTimeout(() => {
          isScrollingRef.current = false;
        }, 50);
      }
      // If scrolled to the beginning (first set), reset to middle (second set)
      else if (scrollLeft <= 50) {
        isScrollingRef.current = true;
        container.scrollLeft = singleSetWidth + scrollLeft;
        setTimeout(() => {
          isScrollingRef.current = false;
        }, 50);
      }
    };

    container.addEventListener("scroll", handleScroll);
    initializeScroll();

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [products]);

  // Set up drag functionality
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || products.length === 0) return;

    // Handle mouse drag start
    const handleMouseDown = (e) => {
      // Only start dragging if clicking on the container or its children (not on buttons/links)
      if (
        e.target.tagName === "BUTTON" ||
        e.target.closest("button") ||
        e.target.tagName === "A" ||
        e.target.closest("a")
      ) {
        return;
      }

      isDraggingRef.current = true;
      startXRef.current = e.pageX - container.offsetLeft;
      scrollLeftRef.current = container.scrollLeft;
      container.style.cursor = "grabbing";
      container.style.userSelect = "none";
    };

    // Handle mouse drag move
    const handleMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startXRef.current) * 2; // Multiply by 2 for faster scrolling
      const newScrollLeft = scrollLeftRef.current - walk;

      // Check for infinite scroll boundaries during drag
      const scrollWidth = container.scrollWidth;
      const singleSetWidth = scrollWidth / 3;

      // If we're dragging past boundaries, handle infinite scroll reset
      if (newScrollLeft >= singleSetWidth * 2 - 50) {
        // Dragging past end - reset to middle set
        container.scrollLeft =
          singleSetWidth + (newScrollLeft - singleSetWidth * 2);
        scrollLeftRef.current = container.scrollLeft;
        startXRef.current = e.pageX - container.offsetLeft;
      } else if (newScrollLeft <= 50) {
        // Dragging past beginning - reset to middle set
        container.scrollLeft = singleSetWidth + newScrollLeft;
        scrollLeftRef.current = container.scrollLeft;
        startXRef.current = e.pageX - container.offsetLeft;
      } else {
        container.scrollLeft = newScrollLeft;
      }
    };

    // Handle mouse drag end
    const handleMouseUp = () => {
      isDraggingRef.current = false;
      container.style.cursor = "grab";
      container.style.userSelect = "";
    };

    // Handle mouse leave (stop dragging if mouse leaves container)
    const handleMouseLeave = () => {
      isDraggingRef.current = false;
      container.style.cursor = "grab";
      container.style.userSelect = "";
    };

    // Add event listeners
    container.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    container.addEventListener("mouseleave", handleMouseLeave);

    // Set initial cursor style
    container.style.cursor = "grab";

    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      container.removeEventListener("mouseleave", handleMouseLeave);
      container.style.cursor = "";
      container.style.userSelect = "";
    };
  }, [products]);

  return (
    <div className="flex flex-col justify-start w-full content__products">
      <div className="relative w-full xl:flex xl:items-center xl:gap-4">
        {/* Mobile / tablet arrows – overlap the cards */}
        <button
          type="button"
          className="absolute z-10 flex items-center justify-center w-10 h-10 px-2 text-xl -translate-y-1/2 shadow-md opacity-100 left-3 top-1/2 md:h-12 md:w-12 md:text-2xl lg:h-14 lg:w-14 lg:text-3xl lg:px-3 xl:hidden text-brand-primary-600 bg-white/80"
          onClick={() => scrollToNextCard(-1)}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>

        <button
          type="button"
          className="absolute z-10 flex items-center justify-center w-10 h-10 px-2 text-xl -translate-y-1/2 shadow-md opacity-100 right-3 top-1/2 md:h-12 md:w-12 md:text-2xl lg:h-14 lg:w-14 lg:text-3xl lg:px-3 xl:hidden text-brand-primary-600 bg-white/80"
          onClick={() => scrollToNextCard(1)}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>

        {/* Desktop (xl+) gutters with arrows inside parent but outside slider */}
        <div className="items-center justify-center hidden xl:flex xl:flex-none">
          <button
            type="button"
            className="flex items-center justify-center w-10 h-10 px-2 text-xl shadow-md md:h-12 md:w-12 md:text-2xl lg:h-14 lg:w-14 lg:text-3xl lg:px-3 xl:h-16 xl:w-16 xl:text-4xl xl:px-4 text-brand-primary-600 bg-white/80"
            onClick={() => scrollToNextCard(-1)}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
        </div>

        <div
          ref={scrollContainerRef}
          className="w-full pb-2 overflow-x-auto product-slider-scroll-container xl:flex-1"
        >
          <style>{`
          .product-slider-scroll-container {
            scrollbar-width: thin;
            scrollbar-color: transparent transparent;
          }
          .product-slider-scroll-container::-webkit-scrollbar {
            height: 8px;
          }
          .product-slider-scroll-container::-webkit-scrollbar-track {
            background: transparent;
          }
          .product-slider-scroll-container::-webkit-scrollbar-thumb {
            background: transparent;
            border-radius: 4px;
          }
          .product-slider-scroll-container:hover::-webkit-scrollbar-thumb,
          .product-slider-scroll-container:active::-webkit-scrollbar-thumb {
            background: #d8dada;
          }
          .product-slider-scroll-container:hover::-webkit-scrollbar-thumb:hover {
            background: #9ca3af;
          }
        `}</style>
          <div ref={cardsContainerRef} className="flex items-stretch gap-6 ">
            {/* Render products 3 times to create seamless infinite scroll */}
            {[...products, ...products, ...products].map((product, index) => (
              <div
                key={`product-${index}`}
                className="flex-shrink-0 w-full sm:w-full sm:min-w-full md:w-[calc((100%-1.5rem)/2)] md:min-w-[calc((100%-1.5rem)/2)] lg:w-[calc((100%-1.5rem)/2)] lg:min-w-[calc((100%-1.5rem)/2)] xl:w-[calc((100%-3rem)/3)] xl:min-w-[calc((100%-3rem)/3)] h-full"
              >
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        </div>

        <div className="items-center justify-center hidden xl:flex xl:flex-none">
          <button
            type="button"
            className="flex items-center justify-center w-10 h-10 px-2 text-xl shadow-md md:h-12 md:w-12 md:text-2xl lg:h-14 lg:w-14 lg:text-3xl lg:px-3 xl:h-16 xl:w-16 xl:text-4xl xl:px-4 text-brand-primary-600 bg-white/80"
            onClick={() => scrollToNextCard(1)}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductSlider;
